import type { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";

const QUOTE_COLUMNS_INITIAL_STATE: GridInitialState = {
  columns: {
    columnVisibilityModel: {
      decisionBehavior: false,
      description: false,
      productTitle: false,
      productTitleVisibility: false,
      productDescription: false,
      productDescriptionVisibility: false,
      manufacturerArticleNumber: false,
      productSupplier: false,
      productBrandLine: false,
      productCategory: false,
      productGtin: false,
      productSapConfig: false,
      materialFactor: false,
      workFactor: false,
    },
  },
};
import { useTranslate } from "@tolgee/react";
import React from "react";
import type {
  ContractType,
  PermissionName,
} from "../../../../clients/graphqlTypes.js";
import { ProjectQuoteDataGrid_ItemFragment } from "../../../main-routes/projects/ProjectQuoteDataGrid.generated.js";
import { useProjectQuoteContext } from "../../../main-routes/projects/ProjectQuoteContext.js";
import {
  useHasSapS4HanaIntegration,
  useFisSolr,
} from "../../sap-s4-hana/hooks.js";
import { createContingencyCheckboxColumn } from "../columns/contingencyCheckboxColumn.js";
import { createDecisionBehaviorColumn } from "../columns/decisionBehaviorColumn.js";
import { createDecisionOptionColumn } from "../columns/decisionOptionColumn.js";
import { createDecisionWizardButtonsColumn } from "../columns/decisionWizardButtonsColumn.js";
import { createTemplatePlaceholderColumn } from "../columns/templatePlaceholderColumn.js";
import { createDescriptionColumn } from "../columns/descriptionColumn.js";
import { createEstimatedQuantityColumn } from "../columns/estimatedQuantityColumn.js";
import { createEstimatedTimeColumn } from "../columns/estimatedTimeColumn.js";
import { createMaterialBuyingPriceColumn } from "../columns/materialBuyingPriceColumn.js";
import { createMaterialFactorColumn } from "../columns/materialFactorColumn.js";
import { createMaterialMarginColumn } from "../columns/materialMarginColumn.js";
import { createMaterialPricePerUnitColumn } from "../columns/materialPricePerUnitColumn.js";
import { createPricePerUnitColumn } from "../columns/pricePerUnitColumn.js";
import { createPriceSubTotalColumn } from "../columns/priceSubTotalColumn.js";
import { createSavingIndicatorColumn } from "../columns/savingIndicatorColumn.js";
import { createQuantityUnitColumn } from "../columns/quantityUnitColumn.js";
import { createTimeTotalColumn } from "../columns/timeTotalColumn.js";
import { createWorkFactorColumn } from "../columns/workFactorColumn.js";
import { createWorkMarginColumn } from "../columns/workMarginColumn.js";
import { createWorkPricePerUnitColumn } from "../columns/workPricePerUnitColumn.js";
import { createWorkSellingRateColumn } from "../columns/workSellingRateColumn.js";
import { createProductArticleNumberColumn } from "../columns/productArticleNumberColumn.js";
import { createProductTitleColumn } from "../columns/productTitleColumn.js";
import { createProductTitleVisibilityColumn } from "../columns/productTitleVisibilityColumn.js";
import { createProductDescriptionColumn } from "../columns/productDescriptionColumn.js";
import { createProductDescriptionVisibilityColumn } from "../columns/productDescriptionVisibilityColumn.js";
import { createManufacturerArticleNumberColumn } from "../columns/manufacturerArticleNumberColumn.js";
import { createProductSupplierColumn } from "../columns/productSupplierColumn.js";
import { createProductBrandLineColumn } from "../columns/productBrandLineColumn.js";
import { createProductCategoryColumn } from "../columns/productCategoryColumn.js";
import { createProductGtinColumn } from "../columns/productGtinColumn.js";
import { createProductSapConfigColumn } from "../columns/productSapConfigColumn.js";
import type { DocDataGridRow } from "../types.js";
import { useContingencyCheckbox } from "./useContingencyCheckbox.js";
import { useDecisionBehaviorToggle } from "./useDecisionBehaviorToggle.js";
import { useDecisionOptionToggle } from "./useDecisionOptionToggle.js";
import { useModifyItemCalculation } from "./useModifyItemCalculation.js";
import { useModifyItemInfo } from "./useModifyItemInfo.js";
import { useModifyItemProduct } from "./useModifyItemProduct.js";
import { useSetItemProduct } from "./useSetItemProduct.js";

interface Options {
  projectId: string;
  docId: string;
  contractType: ContractType | undefined;
  canEdit: boolean;
  itemCanBeEdited?: (item: ProjectQuoteDataGrid_ItemFragment) => boolean;
  expandedItemIds: string[] | undefined;
  setItemExpanded?: (id: string, expanded: boolean) => void;
  viewerIsContractor: boolean;
  quote: {
    id: string;
    owningSystemOrganisationId: string;
    viewerPermissions: PermissionName[];
  };
}

export function useQuoteColumns(options: Options): {
  columns: GridColDef<DocDataGridRow<ProjectQuoteDataGrid_ItemFragment>>[];
  initialState: GridInitialState;
} {
  const {
    projectId,
    docId,
    canEdit,
    itemCanBeEdited,
    expandedItemIds,
    viewerIsContractor,
    quote,
  } = options;

  // Ref-based expand callback — avoids columns useMemo dependency on expandedStore
  const setItemExpandedRef = React.useRef(options.setItemExpanded);
  setItemExpandedRef.current = options.setItemExpanded;

  const onTemplatePlaceholderCompleted = React.useCallback((itemId: string) => {
    setItemExpandedRef.current?.(itemId, true);
  }, []);

  const { refetchWizardQuestions } = useProjectQuoteContext();

  const { modifyItemCalculation } = useModifyItemCalculation({
    projectId,
    docId,
  });

  const { modifyItemInfo } = useModifyItemInfo({ projectId, docId });

  const { modifyItemProduct, createItemProduct } = useModifyItemProduct({
    projectId,
    docId,
  });

  const { setItemProduct } = useSetItemProduct({
    projectId,
    docId,
    expandedItemIds,
  });

  const onProductSelect = React.useCallback(
    (
      itemId: string,
      product: { articleNumber: string; supplierId?: string | null }
    ) => setItemProduct(itemId, product),
    [setItemProduct]
  );

  const onProductCreate = React.useCallback(
    (itemId: string, values: Parameters<typeof createItemProduct>[1]) =>
      createItemProduct(itemId, values),
    [createItemProduct]
  );

  const { toggleContingency } = useContingencyCheckbox({ projectId, docId });

  const { setDecisionBehavior } = useDecisionBehaviorToggle({
    projectId,
    docId,
  });

  const { toggleDecisionOption } = useDecisionOptionToggle({
    projectId,
    docId,
    expandedItemIds,
  });

  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();
  const { showFisSolr, sapIntegrationSettings } = useFisSolr();

  const { t } = useTranslate(["QuoteItem", "Global", "Product"]);

  const columns = React.useMemo(
    (): GridColDef<DocDataGridRow<ProjectQuoteDataGrid_ItemFragment>>[] => [
      //////////////////////////////////////////////////////////////////////////
      // Main columns
      //////////////////////////////////////////////////////////////////////////

      // Contingency checkbox
      createContingencyCheckboxColumn({
        headerName: t("Optional item", { ns: "QuoteItem" }),
        canEdit,
        toggleContingency,
      }),

      // Decision option radio/checkbox
      createDecisionOptionColumn({
        headerName: t("Decision option", { ns: "QuoteItem" }),
        canEdit,
        toggleDecisionOption,
      }),

      // Template placeholder button
      createTemplatePlaceholderColumn({
        headerName: t("Template placeholder", { ns: "QuoteItem" }),
        canEdit,
        projectId,
        docId,
        refetchWizardQuestions,
        onCompleted: onTemplatePlaceholderCompleted,
      }),

      // Decision wizard buttons
      createDecisionWizardButtonsColumn({
        projectId,
        quote,
        canEdit,
        headerName: t("Wizard button", { ns: "QuoteItem" }),
      }),

      // Decision behavior
      createDecisionBehaviorColumn({
        canEdit,
        setDecisionBehavior,
        headerName: t("Decision type", { ns: "QuoteItem" }),
      }),

      // Description
      createDescriptionColumn({
        canEdit,
        modifyItemInfo,
        itemCanBeEdited,
        headerName: t("Description", { ns: "QuoteItem" }),
      }),

      //////////////////////////////////////////////////////////////////////////
      // Product columns
      //////////////////////////////////////////////////////////////////////////

      // Product article number (with replace/create button)
      createProductArticleNumberColumn<ProjectQuoteDataGrid_ItemFragment>({
        headerName: t("Article no.", { ns: "Product" }),
        canEdit,
        showFisSolr,
        sapIntegrationSettings,
        onProductSelect,
        onProductCreate,
        projectId,
        docId,
      }),

      // Product title
      createProductTitleColumn<ProjectQuoteDataGrid_ItemFragment>({
        headerName: t("Product title", { ns: "QuoteItem" }),
        canEdit,
        modifyItemProduct,
      }),

      // Product title visibility (contractor only)
      ...(viewerIsContractor
        ? [
            createProductTitleVisibilityColumn<ProjectQuoteDataGrid_ItemFragment>(
              {
                headerName: t("Title visibility", { ns: "Product" }),
                canEdit,
                viewerIsContractor,
                modifyItemProduct,
              }
            ),
          ]
        : []),

      // Product description
      createProductDescriptionColumn<ProjectQuoteDataGrid_ItemFragment>({
        headerName: t("Product description", { ns: "QuoteItem" }),
        canEdit,
        modifyItemProduct,
      }),

      // Product description visibility (contractor only)
      ...(viewerIsContractor
        ? [
            createProductDescriptionVisibilityColumn<ProjectQuoteDataGrid_ItemFragment>(
              {
                headerName: t("Description visibility", { ns: "Product" }),
                canEdit,
                viewerIsContractor,
                modifyItemProduct,
              }
            ),
          ]
        : []),

      // Manufacturer article number
      createManufacturerArticleNumberColumn<ProjectQuoteDataGrid_ItemFragment>({
        headerName: t("Manufacturer article no.", { ns: "Product" }),
        canEdit,
        modifyItemProduct,
      }),

      // Product supplier
      createProductSupplierColumn<ProjectQuoteDataGrid_ItemFragment>({
        headerName: t("Supplier", { ns: "Product" }),
      }),

      // Product brand / brand line
      createProductBrandLineColumn<ProjectQuoteDataGrid_ItemFragment>({
        headerName: t("Brand", { ns: "Product" }),
      }),

      // Product category
      createProductCategoryColumn<ProjectQuoteDataGrid_ItemFragment>({
        headerName: t("Category", { ns: "Product" }),
      }),

      // Product GTIN
      createProductGtinColumn<ProjectQuoteDataGrid_ItemFragment>({
        headerName: t("GTIN", { ns: "Product" }),
      }),

      // SAP config (conditional)
      ...(hasSapS4HanaIntegration
        ? [
            createProductSapConfigColumn<ProjectQuoteDataGrid_ItemFragment>({
              headerName: "",
              projectId,
              docId,
            }),
          ]
        : []),

      //////////////////////////////////////////////////////////////////////////
      // Calculation columns
      //////////////////////////////////////////////////////////////////////////

      // Estimated quantity
      createEstimatedQuantityColumn({
        projectId,
        docId,
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Qty", { ns: "QuoteItem" }),
      }),

      // Quantity unit
      createQuantityUnitColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Unit", { ns: "QuoteItem" }),
      }),

      // Price per unit
      createPricePerUnitColumn({
        headerName: t("Price/Unit", { ns: "QuoteItem" }),
      }),

      // Material buying price
      createMaterialBuyingPriceColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Mat. price", { ns: "QuoteItem" }),
      }),

      // Material margin
      createMaterialMarginColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Mat. margin", { ns: "QuoteItem" }),
      }),

      // Material factor
      createMaterialFactorColumn({
        headerName: `${t("Mat.", { ns: "QuoteItem" })} ×`,
      }),

      // Material price per unit
      createMaterialPricePerUnitColumn({
        headerName: t("Mat./Unit", { ns: "QuoteItem" }),
      }),

      // Estimated time
      createEstimatedTimeColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Work time", { ns: "QuoteItem" }),
      }),

      // Work selling rate
      createWorkSellingRateColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Work rate", { ns: "QuoteItem" }),
      }),

      // Work margin (popover)
      createWorkMarginColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Work margin", { ns: "QuoteItem" }),
      }),

      // Work factor
      createWorkFactorColumn({
        headerName: `${t("Work", { ns: "QuoteItem" })} ×`,
      }),

      // Work price per unit
      createWorkPricePerUnitColumn({
        headerName: t("Work/Unit", { ns: "QuoteItem" }),
      }),

      // Time total
      createTimeTotalColumn({
        headerName: t("Time total", { ns: "QuoteItem" }),
      }),

      // Price sub-total
      createPriceSubTotalColumn({
        headerName: t("Subtotal", { ns: "QuoteItem" }),
      }),

      // Saving indicator (last column — spinner shown while row mutation is in flight)
      createSavingIndicatorColumn({
        headerName: t("Saving indicator", { ns: "Global" }),
      }),
    ],
    [
      projectId,
      quote,
      t,
      docId,
      canEdit,
      modifyItemCalculation,
      modifyItemInfo,
      modifyItemProduct,
      toggleContingency,
      toggleDecisionOption,
      setDecisionBehavior,
      itemCanBeEdited,
      viewerIsContractor,
      onProductSelect,
      onProductCreate,
      showFisSolr,
      sapIntegrationSettings,
      hasSapS4HanaIntegration,
      refetchWizardQuestions,
      onTemplatePlaceholderCompleted,
    ]
  );

  return { columns, initialState: QUOTE_COLUMNS_INITIAL_STATE };
}
